import { useModal } from '@billy/design-system'
import {
  createContext,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { MINIMAL_INVOICE_NUMBER } from '@features/invoices/constants/minimalInvoiceNumber'

import { ModalId } from '../../../../../../../../../../../enums/modalId'
import { useInvoiceEditorForm } from '../../../../../../../contexts/invoiceEditorFormContext'

interface ContextState {
  closeQuoteNumberModal: () => void
  registeredQuoteNumber: number
  saveQuoteNumber: () => void
}

const QuoteNumberContext = createContext<ContextState | undefined>(undefined)

interface QuoteNumberContextProps {
  children: ReactNode
}

export const QuoteNumberContextProvider = ({ children }: QuoteNumberContextProps): ReactElement => {
  const { invoiceEditorForm } = useInvoiceEditorForm()
  const [registeredQuoteNumber, setRegisteredQuoteNumber] = useState(
    invoiceEditorForm.getValues('invoiceNumber') ?? MINIMAL_INVOICE_NUMBER,
  )
  const hasModalNumberingValuesBeenSaved = useRef(false)
  const hasInitialFormValuesBeenLoadedRef = useRef(false)

  const { clearErrors, watch, setValue, trigger } = invoiceEditorForm

  const { close: closeQuoteNumberModal } = useModal(ModalId.InvoiceEditorQuoteNumberModal, {
    onClose: () => {
      // When modal is being closed in any other way than "Save" button click,
      // form values will be reverted to the initial - "registered" ones.
      if (!hasModalNumberingValuesBeenSaved.current) {
        setValue('invoiceNumber', registeredQuoteNumber)
      }
      clearErrors('invoiceNumber')
      hasModalNumberingValuesBeenSaved.current = false
    },
  })

  const quoteNumberFormValue = watch('invoiceNumber')

  const saveQuoteNumber = useCallback(async () => {
    const isFormValid = await trigger('invoiceNumber')

    if (isFormValid && quoteNumberFormValue !== undefined) {
      hasModalNumberingValuesBeenSaved.current = true
      // On "Save", we need to align form values with the registered values,
      // which are displayed in the Invoice Header.
      setRegisteredQuoteNumber(quoteNumberFormValue)
      closeQuoteNumberModal()
    }
  }, [closeQuoteNumberModal, quoteNumberFormValue, trigger])

  useEffect(() => {
    const shouldRegisterInitialFormValues =
      !hasInitialFormValuesBeenLoadedRef.current && quoteNumberFormValue !== undefined

    if (shouldRegisterInitialFormValues) {
      setRegisteredQuoteNumber(quoteNumberFormValue)
      hasInitialFormValuesBeenLoadedRef.current = true
    }
  }, [quoteNumberFormValue])

  const contextValue = useMemo(
    () => ({
      closeQuoteNumberModal,
      registeredQuoteNumber,
      saveQuoteNumber,
    }),
    [closeQuoteNumberModal, registeredQuoteNumber, saveQuoteNumber],
  )

  return <QuoteNumberContext.Provider value={contextValue}>{children}</QuoteNumberContext.Provider>
}

export const useQuoteNumber = () => {
  const context = useContext(QuoteNumberContext)

  if (!context) {
    throw new Error('QuoteNumberContextProvider is missing in the module!')
  }

  return context
}
